import { Box, Paper, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ColumnVisibilityButton from "../../../components/button/ColumnVisibilityButton";
import RefreshButton from "../../../components/button/RefreshButton";
import { TableHeaderContainer } from "../../../components/container/TableHeaderContainer";
import { TableHeaderToolsContainer } from "../../../components/container/TableHeaderToolsContainer";
import { TableTitleContainer } from "../../../components/container/TableTitleContainer";
import ColumnMenu from "../../../components/context-menu/ColumnMenu";
import AgGrid from "../../../components/grid/AgGrid";
import { defaultColDef, floatFormater, leftAlignColDef, rightAlignColDef } from "../../../components/grid/columnDefinitions/columnDefinitions";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import { computeShearingLayerRows } from "./shearingLayers";

// Building-only. Structural Circularity Indicator table — same AgGrid + toolbar shell as
// ProjectMCICalculationTable. Rows come from computeShearingLayerRows (shearingLayers.js —
// shared with BCI Calculation so both stay in sync): the 15 WBLCA subcategories grouped by
// Shearing Layer, with MCI/PCI/Mass shown per subcategory (every row). SCI
// (theoretical)/(practical) are computed PER SHEARING LAYER (five values, one per layer)
// but shown only ONCE per layer — on that layer's first row — rather than repeated on
// every subcategory row belonging to it:
//   SCI (theoretical) = Σ(MCI × Mass) / Σ(Mass)   — summed over that layer's rows only
//   SCI (practical)   = Σ(PCI × Mass) / Σ(Mass)   — summed over that layer's rows only
const BuildingSCICalculationTable = ({ combined_data, processes = [], activeTab, tabValue }) => {
  const { columnVisibilities } = useContextProvider();
  const { t } = useTranslation();
  const colVisibility_key = "SCI_calculation_table_column_visibility";
  const gridHeaderContainerRef = useRef(null);
  const [columnMenuVisible, setColumnMenuVisible] = useState(false);

  const rows = useMemo(() => {
    const baseRows = computeShearingLayerRows(combined_data, processes);
    const seenLayers = new Set();
    return baseRows.map((row) => {
      if (seenLayers.has(row.shearingLayer)) {
        // Not the layer's first row — blank the SCI columns so the value isn't repeated.
        return { ...row, sciTheoretical: null, sciPractical: null };
      }
      seenLayers.add(row.shearingLayer);
      return row;
    });
  }, [combined_data, processes]);

  useEffect(() => {
    if (activeTab && tabValue && activeTab !== tabValue) {
      setColumnMenuVisible(false);
    }
  }, [activeTab, tabValue]);

  const colDefs = useMemo(() => {
    const isHidden = (field) => {
      if (field === "si_no") return false;
      return !columnVisibilities[colVisibility_key]?.includes(field);
    };
    return [
      {
        headerName: t("SI No."),
        valueGetter: (params) => {
          if (params.node.rowPinned === "bottom") return "";
          return params.node.rowIndex + 1;
        },
        width: 80,
        minWidth: 60,
        headerClass: "ag-center-aligned-header",
        cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
        suppressMenu: true,
        sortable: false,
        field: "si_no"
      },
      { headerName: t("Shearing Layer"), field: "shearingLayer", ...leftAlignColDef, minWidth: 150 },
      { headerName: t("Product Name"), field: "description", ...leftAlignColDef, flex: 1, minWidth: 180 },
      {
        headerName: t("MCI"), field: "mciVal", ...rightAlignColDef, minWidth: 90,
        // Total row shows a value for Mass only — every other column is blank there.
        valueFormatter: (params) => (params.node.rowPinned === "bottom" ? "" : floatFormater.valueFormatter(params)),
      },
      {
        headerName: t("PCI"), field: "pciVal", ...rightAlignColDef, minWidth: 90,
        valueFormatter: (params) => (params.node.rowPinned === "bottom" ? "" : floatFormater.valueFormatter(params)),
      },
      {
        headerName: t("Mass of Product (Kg)"), field: "mass", ...rightAlignColDef, minWidth: 160,
        // null here means every material in this subcategory had no real mass
        // (computeShearingLayerRows excludes calculate_mass's quantity-derived fallback
        // estimate from the sum) — show N/A rather than a misleading "0.00".
        valueFormatter: (params) => (params.value === null || params.value === undefined
          ? t("N/A")
          : floatFormater.valueFormatter(params)),
      },
      {
        headerName: t("SCI(Theoretical)"), field: "sciTheoretical", ...rightAlignColDef, minWidth: 160,
        // Per-Shearing-Layer aggregate (not a single grid-wide value) — blank only when
        // that layer has no materials at all.
        valueFormatter: (params) => (params.value === null || params.value === undefined ? "" : Number(params.value).toFixed(2)),
      },
      {
        headerName: t("SCI(Practical)"), field: "sciPractical", ...rightAlignColDef, minWidth: 150,
        valueFormatter: (params) => (params.value === null || params.value === undefined ? "" : Number(params.value).toFixed(2)),
      },
    ].map((col) => ({ ...defaultColDef, ...col, hide: isHidden(col.field) }));
  }, [columnVisibilities, colVisibility_key, t]);
  const defaultCols = colDefs.map((col) => col.field);

  // No single combined "Total" for SCI here — it's five layer-level values, already shown
  // per row above. The Total row only rolls up Mass (a genuinely combinable quantity).
  const totalRow = useMemo(() => ({
    description: t("Total"),
    mass: rows.reduce((s, r) => s + (r.mass || 0), 0),
  }), [rows, t]);

  return (
    <Paper sx={{ border: 1, borderColor: "#e0e0e0", borderRadius: 4, overflow: "hidden" }}>
      <TableHeaderContainer ref={gridHeaderContainerRef}>
        <TableTitleContainer>
          <Typography variant="Table Title">{t("SCI Calculation")}</Typography>
        </TableTitleContainer>
        <TableHeaderToolsContainer>
          <ColumnVisibilityButton onClick={() => setColumnMenuVisible((p) => !p)} />
          <RefreshButton />
        </TableHeaderToolsContainer>
      </TableHeaderContainer>
      <Box sx={{ height: "auto" }}>
        <AgGrid
          columnDefs={colDefs}
          rowData={rows}
          pinnedBottomRowData={[totalRow]}
          headerCheckbox={false}
          domLayout="autoHeight"
        />
      </Box>
      <ColumnMenu
        anchorRef={gridHeaderContainerRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={colDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
      />
    </Paper>
  );
};
export default BuildingSCICalculationTable;
